import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { servicesAPI, timeSlotsAPI, bookingsAPI, discountCodesAPI, siteSettingsAPI } from '../utils/api';
import { toast } from 'react-toastify';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import CardSetupForm from '../components/CardSetupForm';
import LandingSection from '../components/LandingSection';
import FiveDayCalendar from '../components/FiveDayCalendar';
import dayjs from 'dayjs';
import {
  Stepper,
  Step,
  StepLabel,
  Box,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Chip,
  Checkbox,
  IconButton,
  Collapse,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import SpaIcon from '@mui/icons-material/Spa';
import EventIcon from '@mui/icons-material/Event';
import PersonIcon from '@mui/icons-material/Person';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_your_key');

// Booking steps configuration
const bookingSteps = [
  { label: 'Select Service', icon: <SpaIcon /> },
  { label: 'Choose Time', icon: <EventIcon /> },
  { label: 'Your Details', icon: <PersonIcon /> },
  { label: 'Confirm', icon: <CreditCardIcon /> },
];

// Custom Stepper component for booking flow
function BookingStepper({ activeStep }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Compact mobile version - horizontal with small icons, no labels
  if (isMobile) {
    return (
      <Box sx={{
        width: '100%',
        mb: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.5,
      }}>
        {bookingSteps.map((stepConfig, index) => (
          <React.Fragment key={stepConfig.label}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor:
                  index < activeStep
                    ? 'success.main'
                    : index === activeStep
                      ? 'primary.main'
                      : 'grey.200',
                color: index <= activeStep ? 'white' : 'grey.400',
                transition: 'all 0.3s ease',
                boxShadow: index === activeStep
                  ? '0 2px 8px rgba(139, 38, 53, 0.3)'
                  : 'none',
                '& svg': { fontSize: 16 },
              }}
            >
              {stepConfig.icon}
            </Box>
            {index < bookingSteps.length - 1 && (
              <Box
                sx={{
                  width: 24,
                  height: 2,
                  bgcolor: index < activeStep ? 'success.main' : 'grey.300',
                  transition: 'background-color 0.3s ease',
                }}
              />
            )}
          </React.Fragment>
        ))}
      </Box>
    );
  }

  // Desktop version - full stepper with labels
  return (
    <Box sx={{ width: '100%', mb: 4 }}>
      <Stepper
        activeStep={activeStep}
        alternativeLabel
        sx={{
          '& .MuiStepConnector-line': {
            borderColor: theme.palette.grey[300],
          },
          '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': {
            borderColor: theme.palette.primary.main,
          },
          '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
            borderColor: theme.palette.success.main,
          },
        }}
      >
        {bookingSteps.map((stepConfig, index) => (
          <Step key={stepConfig.label} completed={index < activeStep}>
            <StepLabel
              StepIconComponent={() => (
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor:
                      index < activeStep
                        ? 'success.main'
                        : index === activeStep
                          ? 'primary.main'
                          : 'grey.200',
                    color: index <= activeStep ? 'white' : 'grey.500',
                    transition: 'all 0.3s ease',
                    boxShadow: index === activeStep
                      ? '0 4px 12px rgba(139, 38, 53, 0.3)'
                      : 'none',
                  }}
                >
                  {stepConfig.icon}
                </Box>
              )}
            >
              {stepConfig.label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
}

function BookingPage() {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    notes: ''
  });
  const [pendingBooking, setPendingBooking] = useState(null);
  const [setupIntentSecret, setSetupIntentSecret] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [savedCard, setSavedCard] = useState(null);
  const [useSavedCard, setUseSavedCard] = useState(false);
  const [checkingCard, setCheckingCard] = useState(false);
  const [prefilledFromEmail, setPrefilledFromEmail] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [categoryOrder, setCategoryOrder] = useState([]);

  // Discount code state
  const [discountCode, setDiscountCode] = useState('');
  const [discountValidating, setDiscountValidating] = useState(false);
  const [discountApplied, setDiscountApplied] = useState(null); // { code, discount_amount, final_price, etc }

  // Next available slot state
  const [findingNextAvailable, setFindingNextAvailable] = useState(false);

  // Calendar navigation state (5-day view)
  const [calendarStartDate, setCalendarStartDate] = useState(() => {
    // Start from today
    return dayjs().format('YYYY-MM-DD');
  });

  // Calendar view mode: '5day' or 'month'
  const [calendarViewMode, setCalendarViewMode] = useState('5day');

  // Ref for scrolling to booking form
  const bookingFormRef = useRef(null);

  // Get unique categories from services, using custom order from settings
  const categories = ['All', ...new Set(services.map(s => s.category || 'General').filter(Boolean))].sort((a, b) => {
    if (a === 'All') return -1;
    if (b === 'All') return 1;
    // Use custom category order if available
    if (categoryOrder.length > 0) {
      const aIndex = categoryOrder.indexOf(a);
      const bIndex = categoryOrder.indexOf(b);
      if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    }
    return a.localeCompare(b);
  });

  // Filter services by selected category
  const filteredServices = selectedCategory === 'All'
    ? services
    : services.filter(s => (s.category || 'General') === selectedCategory);

  // Calculate totals for selected services
  const totalPrice = selectedServices.reduce((sum, s) => sum + parseFloat(s.price || 0), 0);
  const totalDuration = selectedServices.reduce((sum, s) => sum + parseInt(s.duration || 0, 10), 0);

  const handleCancelBooking = () => {
    // Reset all state back to initial
    setStep(1);
    setSelectedServices([]);
    setSelectedDate('');
    setAvailableSlots([]);
    setSelectedSlot(null);
    setCustomerInfo({ name: '', email: '', phone: '', notes: '' });
    setPendingBooking(null);
    setSetupIntentSecret(null);
    setSavedCard(null);
    setUseSavedCard(false);
    setSelectedCategory('All');
    // Reset discount code
    setDiscountCode('');
    setDiscountApplied(null);
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Validate discount code
  const handleApplyDiscountCode = async () => {
    if (!discountCode.trim() || selectedServices.length === 0) return;

    setDiscountValidating(true);
    try {
      const res = await discountCodesAPI.validate({
        code: discountCode.trim(),
        service_price: totalPrice,
        service_category: selectedServices[0]?.category // Use first service's category
      });

      setDiscountApplied(res.data);
      toast.success(`Discount applied: ${res.data.discount_type === 'percentage' ? res.data.discount_value + '%' : '£' + res.data.discount_value} off!`);
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Invalid discount code';
      toast.error(errorMsg);
      setDiscountApplied(null);
    } finally {
      setDiscountValidating(false);
    }
  };

  // Remove applied discount
  const handleRemoveDiscount = () => {
    setDiscountCode('');
    setDiscountApplied(null);
  };

  useEffect(() => {
    fetchServices();
  }, []);

  // Handle pre-filled booking from email link (e.g., rejection with alternative)
  useEffect(() => {
    if (services.length > 0 && searchParams.get('prefilled') === 'true') {
      const serviceId = searchParams.get('service');
      const date = searchParams.get('date');
      const time = searchParams.get('time');
      const name = searchParams.get('name');
      const email = searchParams.get('email');

      console.log('📧 Pre-fill attempt:', {
        serviceId,
        date,
        time,
        name,
        email,
        availableServices: services.map(s => ({ id: s.id, name: s.name }))
      });

      // Find and select the service
      const service = services.find(s => s.id.toString() === serviceId);
      if (service) {
        console.log('✅ Service found:', service.name);
        setSelectedServices([service]);
        setSelectedDate(date || '');

        // Pre-fill customer info
        setCustomerInfo(prev => ({
          ...prev,
          name: name || '',
          email: email || ''
        }));

        // Check for saved card and VIP status if email is provided
        if (email && email.includes('@') && email.includes('.')) {
          setCheckingCard(true);
          bookingsAPI.checkCard(email)
            .then(response => {
              if (response.data.has_saved_card) {
                setSavedCard(response.data);
                setUseSavedCard(true); // Default to using saved card
                console.log('✅ Saved card found and pre-selected');
              } else {
                setSavedCard(null);
                setUseSavedCard(false);
              }
            })
            .catch(error => {
              console.error('Error checking for saved card:', error);
              setSavedCard(null);
            })
            .finally(() => {
              setCheckingCard(false);
            });
        }

        // If we have date and time, create a pre-selected slot
        if (date && time) {
          setSelectedSlot({ start_time: time });
          setPrefilledFromEmail(true);
          // Go directly to customer details step
          setStep(3);
          console.log('✅ Jumping to step 3 with pre-filled data');
        } else if (date) {
          setStep(2);
        } else {
          setStep(2);
        }
      } else {
        console.error('❌ Service not found with ID:', serviceId);
        toast.warning('Service not found. Please select a service from the list below.');
      }
    }
  }, [services, searchParams]);

  useEffect(() => {
    if (selectedDate && selectedServices.length > 0) {
      fetchAvailableSlots();
    }
  }, [selectedDate, selectedServices]);

  const fetchServices = async () => {
    try {
      const [servicesResponse, settingsResponse] = await Promise.all([
        servicesAPI.getAll(),
        siteSettingsAPI.getAll()
      ]);
      setServices(servicesResponse.data);

      // Set category order from settings
      if (settingsResponse.data.category_order) {
        const order = typeof settingsResponse.data.category_order === 'string'
          ? JSON.parse(settingsResponse.data.category_order)
          : settingsResponse.data.category_order;
        setCategoryOrder(order || []);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const fetchAvailableSlots = async () => {
    try {
      setLoading(true);
      const response = await timeSlotsAPI.getAvailable(selectedDate, totalDuration);
      setAvailableSlots(response.data);
    } catch (error) {
      console.error('Error fetching slots:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleServiceSelect = (service, proceed = false) => {
    setSelectedServices(prev => {
      const isSelected = prev.some(s => s.id === service.id);
      if (isSelected) {
        // Remove service
        return prev.filter(s => s.id !== service.id);
      } else {
        // Add service
        return [...prev, service];
      }
    });
    // If proceed is true, move to step 2 (used by Continue button)
    if (proceed) {
      setStep(2);
    }
  };

  const handleRemoveService = (serviceId) => {
    setSelectedServices(prev => prev.filter(s => s.id !== serviceId));
  };

  const handleProceedToTime = () => {
    if (selectedServices.length > 0) {
      setStep(2);
    } else {
      toast.error('Please select at least one service');
    }
  };

  const handleDateChange = (newDate) => {
    // Handle string date (from FiveDayCalendar), dayjs object, or event
    if (typeof newDate === 'string') {
      setSelectedDate(newDate);
    } else if (newDate && newDate.$d) {
      // dayjs object
      setSelectedDate(newDate.format('YYYY-MM-DD'));
    } else if (newDate?.target?.value) {
      // regular input event
      setSelectedDate(newDate.target.value);
    }
    setSelectedSlot(null);
  };

  // Navigate the 5-day calendar view
  const handleCalendarNavigate = (days) => {
    setCalendarStartDate(prev => dayjs(prev).add(days, 'day').format('YYYY-MM-DD'));
  };

  // Find the next available slot across upcoming days
  const findNextAvailable = async () => {
    if (selectedServices.length === 0) return;

    setFindingNextAvailable(true);
    const maxDaysToCheck = 30; // Look up to 30 days ahead
    let currentDate = new Date();

    // Start from tomorrow if it's after 5pm
    if (currentDate.getHours() >= 17) {
      currentDate.setDate(currentDate.getDate() + 1);
    }

    try {
      for (let i = 0; i < maxDaysToCheck; i++) {
        const dateStr = currentDate.toISOString().split('T')[0];

        const response = await timeSlotsAPI.getAvailable(dateStr, totalDuration);
        const slots = response.data;

        if (slots && slots.length > 0) {
          // Found available slots - show them but don't auto-select
          setSelectedDate(dateStr);
          setAvailableSlots(slots);
          // Don't auto-select - let user choose
          // Update calendar view to show this date
          setCalendarStartDate(dateStr);
          toast.success(`Found slot on ${new Date(dateStr).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })} - tap to select`);
          setFindingNextAvailable(false);
          return;
        }

        // Move to next day
        currentDate.setDate(currentDate.getDate() + 1);
      }

      // No slots found in the next 30 days
      toast.info('No available slots found in the next 30 days. Please contact us directly.');
    } catch (error) {
      console.error('Error finding next available:', error);
      toast.error('Error searching for available slots');
    } finally {
      setFindingNextAvailable(false);
    }
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    setStep(3);
  };

  const handleCustomerInfoChange = async (e) => {
    const { name, value } = e.target;
    setCustomerInfo({
      ...customerInfo,
      [name]: value
    });

    // Check for saved card and VIP status when email is entered (valid email format)
    if (name === 'email' && value.includes('@') && value.includes('.')) {
      setCheckingCard(true);
      try {
        const response = await bookingsAPI.checkCard(value);
        if (response.data.has_saved_card) {
          setSavedCard(response.data);
          setUseSavedCard(true); // Default to using saved card
        } else {
          setSavedCard(null);
          setUseSavedCard(false);
        }
      } catch (error) {
        console.error('Error checking for saved card:', error);
        setSavedCard(null);
      } finally {
        setCheckingCard(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate phone - must contain at least 10 digits
    if (customerInfo.phone) {
      const digitsOnly = customerInfo.phone.replace(/[^0-9]/g, '');
      if (digitsOnly.length < 10) {
        toast.error('Please enter a valid phone number (at least 10 digits)');
        return;
      }
    }

    setLoading(true);

    try {
      const bookingData = {
        customer: customerInfo,
        service_id: selectedServices[0]?.id, // Primary service for backwards compatibility
        service_ids: selectedServices.map(s => s.id), // All selected services
        date: selectedDate,
        start_time: selectedSlot.start_time,
        total_duration: totalDuration,
        total_price: totalPrice,
        use_saved_card: useSavedCard,
        saved_payment_method_id: useSavedCard && savedCard ? savedCard.stripe_payment_method_id : null,
        admin_suggested: searchParams.get('admin_suggested') === 'true', // Auto-approve if from smart link
        // Discount code if applied
        discount_code: discountApplied?.code || null,
        discount_amount: discountApplied?.discount_amount || 0
      };

      const response = await bookingsAPI.create(bookingData);

      if (response.data.requires_card_setup) {
        // Go to card setup step
        setPendingBooking(response.data.booking);
        setSetupIntentSecret(response.data.setup_intent_client_secret);
        setStep(4);
      } else {
        // Booking confirmed with saved card
        setSuccess(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Booking failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCardSetupSuccess = () => {
    setSuccess(true);
    setPendingBooking(null);
    setSetupIntentSecret(null);
  };

  const handleCardSetupCancel = () => {
    // Note: Booking is already created but pending. Going back won't cancel it.
    // Could add an API call to cancel the pending booking here if desired.
    setStep(3);
    setPendingBooking(null);
    setSetupIntentSecret(null);
  };

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  if (success) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📩</div>
          <h2>Booking Request Received!</h2>
          <p style={{ marginTop: '1rem', color: '#6B6B6B' }}>
            Thank you! Your booking request has been submitted.
          </p>

          {/* Booking Summary */}
          <div style={{
            background: '#FAF8F5',
            borderRadius: '8px',
            padding: '1rem',
            margin: '1.5rem 0',
            border: '1px solid #E8C9A5'
          }}>
            {selectedServices.map((service, idx) => (
              <p key={service.id} style={{ margin: idx === 0 ? '0 0 0.25rem' : '0.25rem 0', fontWeight: idx === 0 ? 600 : 400, fontSize: idx === 0 ? '1rem' : '0.9rem' }}>
                {service.name} {selectedServices.length > 1 && <span style={{ color: '#6B6B6B' }}>({service.duration} min)</span>}
              </p>
            ))}
            <p style={{ margin: '0.5rem 0 0.5rem', color: '#6B6B6B' }}>{selectedDate} at {selectedSlot?.start_time}</p>
            <p style={{ margin: '0', color: '#6B6B6B', fontSize: '0.85rem' }}>Total duration: {totalDuration} minutes</p>
            {discountApplied ? (
              <div style={{ textAlign: 'center', marginTop: '0.5rem' }}>
                <p style={{ margin: '0.5rem 0 0', textDecoration: 'line-through', color: '#999' }}>
                  £{totalPrice.toFixed(2)}
                </p>
                <p style={{ margin: '0', color: '#4A7C59', fontSize: '0.9rem' }}>
                  Discount ({discountApplied.code}): -£{discountApplied.discount_amount.toFixed(2)}
                </p>
                <p style={{ margin: '0.25rem 0 0', fontWeight: 600, fontSize: '1.25rem', color: '#8B2635' }}>
                  £{discountApplied.final_price.toFixed(2)}
                </p>
              </div>
            ) : (
              <p style={{ margin: '0.5rem 0 0', fontWeight: 600, fontSize: '1.1rem' }}>
                £{totalPrice.toFixed(2)}
              </p>
            )}
          </div>

          {/* What happens next */}
          <div style={{
            background: '#F0F7F1',
            border: '1px solid #A8C9AD',
            borderRadius: '8px',
            padding: '1.25rem',
            margin: '1.5rem 0',
            textAlign: 'left'
          }}>
            <h4 style={{ margin: '0 0 0.75rem 0', color: '#4A7C59' }}>What happens next?</h4>
            <ol style={{ margin: 0, paddingLeft: '1.25rem', color: '#6B6B6B', fontSize: '0.9rem', lineHeight: '1.6' }}>
              <li>We'll review your booking request</li>
              <li>You'll receive a <strong>confirmation email</strong> at <strong>{customerInfo.email}</strong> once approved</li>
              <li>Your appointment is only confirmed after you receive this email</li>
            </ol>
          </div>

          <p style={{ color: '#6B6B6B', fontSize: '0.85rem' }}>
            💳 Your card is saved securely and will <strong>not</strong> be charged unless you miss a confirmed appointment without 24 hours notice.
          </p>
          <button
            className="btn btn-primary"
            style={{ marginTop: '1.5rem' }}
            onClick={() => window.location.href = '/'}
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  // Step 4: Card Setup Form
  if (step === 4 && pendingBooking && setupIntentSecret) {
    return (
      <div className="container">
        <Elements stripe={stripePromise}>
          <CardSetupForm
            booking={pendingBooking}
            clientSecret={setupIntentSecret}
            onSuccess={handleCardSetupSuccess}
            onCancel={handleCardSetupCancel}
          />
        </Elements>
      </div>
    );
  }

  const scrollToBookingForm = () => {
    bookingFormRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="container">
      {/* Landing Section - only show on step 1 */}
      {step === 1 && (
        <LandingSection onBookNow={scrollToBookingForm} />
      )}

      <div className="card" ref={bookingFormRef}>
        <h1 style={{ marginBottom: '2rem', textAlign: 'center' }}>Book Your Appointment</h1>

        {/* Progress Stepper */}
        <BookingStepper activeStep={step - 1} />

        {/* Step 1: Select Service - Multi-Select */}
        {step === 1 && (
          <div>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Select Services
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {services.length} services available • Select one or more
            </Typography>

            {/* Selected Services Panel */}
            <Collapse in={selectedServices.length > 0}>
              <Box sx={{
                mb: 3,
                p: 2,
                bgcolor: '#F0F7F1',
                borderRadius: 2,
                border: '2px solid #4A7C59',
              }}>
                <Typography fontWeight={600} color="#4A7C59" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon fontSize="small" />
                  Selected Services ({selectedServices.length})
                </Typography>
                {selectedServices.map(service => (
                  <Box
                    key={service.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 0.75,
                      borderBottom: '1px solid rgba(74, 124, 89, 0.2)',
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <Box>
                      <Typography fontWeight={500} fontSize="0.95rem">{service.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{service.duration} min</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography fontWeight={600} color="primary.main">£{service.price}</Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveService(service.id)}
                        sx={{ color: '#C62828' }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
                <Box sx={{ mt: 2, pt: 1.5, borderTop: '2px solid rgba(74, 124, 89, 0.3)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Total: {totalDuration} minutes
                    </Typography>
                    <Typography fontWeight={700} fontSize="1.25rem" color="primary.main">
                      £{totalPrice.toFixed(2)}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleProceedToTime}
                    sx={{ px: 4 }}
                  >
                    Continue
                  </Button>
                </Box>
              </Box>
            </Collapse>

            {/* Category Accordions */}
            <Box sx={{
              '& .MuiAccordion-root': {
                boxShadow: 'none',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '12px !important',
                mb: 1.5,
                '&:before': { display: 'none' },
                '&.Mui-expanded': {
                  margin: '0 0 12px 0',
                },
              },
              '& .MuiAccordionSummary-root': {
                minHeight: 56,
                '&.Mui-expanded': { minHeight: 56 },
              },
              '& .MuiAccordionSummary-content': {
                margin: '12px 0',
                '&.Mui-expanded': { margin: '12px 0' },
              },
            }}>
              {categories.filter(c => c !== 'All').map(category => {
                const categoryServices = services.filter(s => (s.category || 'General') === category);
                const selectedInCategory = categoryServices.filter(s => selectedServices.some(sel => sel.id === s.id)).length;
                return (
                  <Accordion key={category} defaultExpanded={categories.length <= 4}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        bgcolor: 'background.paper',
                        '&:hover': { bgcolor: 'rgba(139, 38, 53, 0.04)' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                        <Typography fontWeight={600} sx={{ flex: 1 }}>
                          {category}
                        </Typography>
                        {selectedInCategory > 0 && (
                          <Chip
                            icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                            label={selectedInCategory}
                            size="small"
                            sx={{
                              bgcolor: '#4A7C59',
                              color: 'white',
                              fontWeight: 600,
                              height: 24,
                              '& .MuiChip-icon': { color: 'white' },
                            }}
                          />
                        )}
                        <Chip
                          label={categoryServices.length}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(139, 38, 53, 0.1)',
                            color: 'primary.main',
                            fontWeight: 600,
                            height: 24,
                          }}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      {categoryServices.map((service, idx) => {
                        const isSelected = selectedServices.some(s => s.id === service.id);
                        return (
                          <Box
                            key={service.id}
                            onClick={() => handleServiceSelect(service)}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              p: 2,
                              cursor: 'pointer',
                              borderTop: idx === 0 ? '1px solid' : 'none',
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              transition: 'all 0.15s ease',
                              bgcolor: isSelected ? 'rgba(74, 124, 89, 0.08)' : 'transparent',
                              '&:hover': {
                                bgcolor: isSelected ? 'rgba(74, 124, 89, 0.12)' : 'rgba(212, 165, 116, 0.08)',
                              },
                              '&:last-child': {
                                borderBottom: 'none',
                                borderRadius: '0 0 12px 12px',
                              },
                            }}
                          >
                            {/* Checkbox */}
                            <Checkbox
                              checked={isSelected}
                              sx={{
                                mr: 1,
                                color: 'grey.400',
                                '&.Mui-checked': { color: '#4A7C59' },
                              }}
                            />

                            {/* Service Name */}
                            <Box sx={{ flex: 1, pr: 2 }}>
                              <Typography
                                fontWeight={isSelected ? 600 : 500}
                                sx={{
                                  fontSize: { xs: '0.9rem', sm: '1rem' },
                                  color: isSelected ? '#4A7C59' : 'text.primary',
                                }}
                              >
                                {service.name}
                              </Typography>
                              {service.description && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    mt: 0.5,
                                    fontSize: '0.8rem',
                                    display: { xs: 'none', sm: '-webkit-box' },
                                    WebkitLineClamp: 1,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                  }}
                                >
                                  {service.description}
                                </Typography>
                              )}
                            </Box>

                            {/* Price & Duration */}
                            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                              <Typography
                                fontWeight={700}
                                color="primary.main"
                                sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem' } }}
                              >
                                £{service.price}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: '0.75rem' }}
                              >
                                {service.duration} min
                              </Typography>
                            </Box>

                            {/* Add indicator */}
                            {!isSelected && (
                              <AddIcon sx={{ ml: 1, color: 'grey.400', fontSize: 20 }} />
                            )}
                          </Box>
                        );
                      })}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>

            {services.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                No services available.
              </Box>
            )}

            {/* Floating Total Bar - Fixed at bottom when services selected */}
            {selectedServices.length > 0 && (
              <Box
                sx={{
                  position: 'fixed',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  bgcolor: 'white',
                  borderTop: '2px solid #4A7C59',
                  boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
                  p: 2,
                  zIndex: 1000,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {selectedServices.length} service{selectedServices.length !== 1 ? 's' : ''} • {totalDuration} min
                  </Typography>
                  <Typography fontWeight={700} fontSize="1.25rem" color="primary.main">
                    £{totalPrice.toFixed(2)}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleProceedToTime}
                  sx={{ px: 4, py: 1.5 }}
                >
                  Continue
                </Button>
              </Box>
            )}

            {/* Spacer for fixed footer */}
            {selectedServices.length > 0 && <Box sx={{ height: 100 }} />}
          </div>
        )}

        {/* Step 2: Choose Time */}
        {step === 2 && selectedServices.length > 0 && (
          <div>
            <h2>Choose Date & Time</h2>
            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#FAF8F5', borderRadius: 1, border: '1px solid #E8C9A5' }}>
              <Typography fontWeight={600} gutterBottom>
                Selected Services ({selectedServices.length})
              </Typography>
              {selectedServices.map(service => (
                <Typography key={service.id} variant="body2" sx={{ color: 'text.secondary' }}>
                  • {service.name} ({service.duration} min) - £{service.price}
                </Typography>
              ))}
              <Typography fontWeight={600} sx={{ mt: 1, color: 'primary.main' }}>
                Total: {totalDuration} min • £{totalPrice.toFixed(2)}
              </Typography>
            </Box>

            {/* Calendar View Toggle */}
            <Box sx={{
              display: 'flex',
              gap: 1,
              mb: 2,
            }}>
              <Button
                variant={calendarViewMode === '5day' ? 'contained' : 'outlined'}
                size="small"
                startIcon={<ViewWeekIcon />}
                onClick={() => setCalendarViewMode('5day')}
                sx={{
                  bgcolor: calendarViewMode === '5day' ? 'primary.main' : 'transparent',
                  borderColor: 'primary.main',
                  color: calendarViewMode === '5day' ? 'white' : 'primary.main',
                  '&:hover': {
                    bgcolor: calendarViewMode === '5day' ? 'primary.dark' : 'rgba(139, 38, 53, 0.08)',
                  },
                }}
              >
                5-Day
              </Button>
              <Button
                variant={calendarViewMode === 'month' ? 'contained' : 'outlined'}
                size="small"
                startIcon={<CalendarMonthIcon />}
                onClick={() => setCalendarViewMode('month')}
                sx={{
                  bgcolor: calendarViewMode === 'month' ? 'primary.main' : 'transparent',
                  borderColor: 'primary.main',
                  color: calendarViewMode === 'month' ? 'white' : 'primary.main',
                  '&:hover': {
                    bgcolor: calendarViewMode === 'month' ? 'primary.dark' : 'rgba(139, 38, 53, 0.08)',
                  },
                }}
              >
                Month
              </Button>
            </Box>

            {/* Calendar View Container */}
            <Box sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: { xs: 1.5, sm: 2 },
              bgcolor: 'background.paper',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}>
              {calendarViewMode === '5day' ? (
                <>
                  <FiveDayCalendar
                    selectedDate={selectedDate}
                    onDateSelect={handleDateChange}
                    startDate={calendarStartDate}
                    onNavigate={handleCalendarNavigate}
                  />

                  {/* Quick Actions Row */}
                  <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 1.5,
                    mt: 2,
                    pt: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                  }}>
                    <Button
                      variant="contained"
                      startIcon={findingNextAvailable ? <CircularProgress size={16} color="inherit" /> : <FlashOnIcon />}
                      onClick={findNextAvailable}
                      disabled={findingNextAvailable}
                      sx={{
                        bgcolor: '#4A7C59',
                        '&:hover': { bgcolor: '#3d6a4a' },
                        whiteSpace: 'nowrap',
                        flex: { xs: 1, sm: 'none' },
                      }}
                    >
                      {findingNextAvailable ? 'Searching...' : 'Find Next Available'}
                    </Button>

                    {selectedDate && (
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 1,
                        bgcolor: '#FAF8F5',
                        borderRadius: 1,
                        borderLeft: '3px solid',
                        borderColor: 'primary.main',
                        flex: 1,
                      }}>
                        <EventIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                        <Box>
                          <Typography variant="body2" fontWeight={600} color="primary.main">
                            {dayjs(selectedDate).format('dddd, D MMMM YYYY')}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </>
              ) : (
                <>
                  {/* Month View */}
                  <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                    alignItems: { xs: 'center', md: 'flex-start' },
                  }}>
                    <DateCalendar
                      value={selectedDate ? dayjs(selectedDate) : null}
                      onChange={(newDate) => {
                        if (newDate) {
                          handleDateChange(newDate.format('YYYY-MM-DD'));
                          // Also update 5-day view start date
                          setCalendarStartDate(newDate.format('YYYY-MM-DD'));
                        }
                      }}
                      minDate={dayjs()}
                      sx={{
                        width: '100%',
                        maxWidth: 320,
                        '& .MuiPickersDay-root': {
                          '&.Mui-selected': {
                            backgroundColor: 'primary.main',
                          },
                        },
                      }}
                    />

                    <Box sx={{ flex: 1, width: '100%' }}>
                      <Button
                        variant="contained"
                        startIcon={findingNextAvailable ? <CircularProgress size={16} color="inherit" /> : <FlashOnIcon />}
                        onClick={findNextAvailable}
                        disabled={findingNextAvailable}
                        fullWidth
                        sx={{
                          bgcolor: '#4A7C59',
                          '&:hover': { bgcolor: '#3d6a4a' },
                          mb: 2,
                        }}
                      >
                        {findingNextAvailable ? 'Searching...' : 'Find Next Available'}
                      </Button>

                      {selectedDate && (
                        <Box sx={{
                          p: 2,
                          bgcolor: '#FAF8F5',
                          borderRadius: 1,
                          borderLeft: '3px solid',
                          borderColor: 'primary.main',
                        }}>
                          <Typography variant="body2" fontWeight={600} color="primary.main">
                            {dayjs(selectedDate).format('dddd')}
                          </Typography>
                          <Typography variant="h6" fontWeight={500}>
                            {dayjs(selectedDate).format('D MMMM YYYY')}
                          </Typography>
                        </Box>
                      )}

                      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                        💡 Tip: Use the 5-Day view for quick selection or Find Next Available to jump to the nearest open slot.
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            </Box>

            {selectedDate && (
              <>
                <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Available Times</h3>
                {loading ? (
                  <div className="loading">Loading available slots...</div>
                ) : availableSlots.length === 0 ? (
                  <div className="alert alert-info" style={{ textAlign: 'center', padding: '1.5rem' }}>
                    <p style={{ margin: '0 0 0.5rem 0', fontWeight: '500' }}>
                      No available slots for {new Date(selectedDate).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </p>
                    <p style={{ margin: 0, fontSize: '0.9rem', color: '#6B6B6B' }}>
                      Try selecting a different date using the calendar above.
                    </p>
                  </div>
                ) : (
                  <div className="time-slots-grid">
                    {availableSlots.map((slot, index) => (
                      <div
                        key={`${slot.start_time}-${index}`}
                        className={`time-slot ${selectedSlot?.start_time === slot.start_time ? 'selected' : ''}`}
                        onClick={() => handleSlotSelect(slot)}
                      >
                        <div>{slot.start_time}</div>
                        <small style={{ fontSize: '0.85rem', opacity: 0.8 }}>to {slot.end_time}</small>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setStep(1)}
              >
                Back
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancelBooking}
              >
                Cancel Booking
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Customer Information */}
        {step === 3 && selectedServices.length > 0 && selectedSlot && (
          <div>
            <h2>Your Details</h2>

            {/* Show banner if this is a pre-filled booking from email */}
            {prefilledFromEmail && (
              <div style={{
                background: '#F0F7F1',
                border: '1px solid #A8C9AD',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem'
              }}>
                <span style={{ fontSize: '1.25rem' }}>💡</span>
                <div>
                  <p style={{ margin: 0, fontWeight: '600', color: '#4A7C59' }}>Suggested Time Slot</p>
                  <p style={{ margin: '0.25rem 0 0', fontSize: '0.9rem', color: '#4A7C59' }}>
                    This time was suggested as an alternative. Just confirm your details to book!
                  </p>
                </div>
              </div>
            )}

            <div style={{ marginBottom: '1rem' }}>
              {selectedServices.map((service, idx) => (
                <p key={service.id} style={{ margin: idx === 0 ? 0 : '0.25rem 0' }}>
                  <strong>{service.name}</strong> <span style={{ color: '#6B6B6B' }}>({service.duration} min)</span>
                </p>
              ))}
              <p style={{ marginTop: '0.5rem', color: '#6B6B6B' }}>
                {selectedDate} at {selectedSlot.start_time} • Total: {totalDuration} min
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={customerInfo.name}
                  onChange={handleCustomerInfoChange}
                  placeholder="e.g. Sarah Smith"
                  autoComplete="name"
                  required
                />
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={customerInfo.email}
                  onChange={handleCustomerInfoChange}
                  placeholder="e.g. sarah@example.com"
                  autoComplete="email"
                  required
                />
              </div>

              <div className="form-group">
                <label>Phone *</label>
                <input
                  type="tel"
                  name="phone"
                  value={customerInfo.phone}
                  onChange={handleCustomerInfoChange}
                  placeholder="e.g. 07123 456789"
                  autoComplete="tel"
                  required
                />
                {customerInfo.phone && customerInfo.phone.replace(/[^0-9]/g, '').length < 10 && customerInfo.phone.length > 0 && (
                  <small style={{ color: '#C62828', marginTop: '0.25rem', display: 'block' }}>
                    Please enter a valid phone number (at least 10 digits)
                  </small>
                )}
              </div>

              <div className="form-group">
                <label>Notes (optional)</label>
                <textarea
                  name="notes"
                  value={customerInfo.notes}
                  onChange={handleCustomerInfoChange}
                  placeholder="Any special requests or notes..."
                />
              </div>

              {/* Discount Code */}
              <div className="form-group">
                <label>Discount Code (optional)</label>
                {discountApplied ? (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem 1rem',
                    background: '#F0F7F1',
                    borderRadius: '8px',
                    border: '1px solid #A8C9AD'
                  }}>
                    <span style={{ color: '#4A7C59', fontWeight: 600 }}>
                      {discountApplied.code}
                    </span>
                    <span style={{ color: '#4A7C59' }}>
                      {discountApplied.discount_type === 'percentage'
                        ? `${discountApplied.discount_value}% off`
                        : `£${discountApplied.discount_value} off`}
                    </span>
                    <span style={{ color: '#4A7C59', fontWeight: 600 }}>
                      (-£{discountApplied.discount_amount.toFixed(2)})
                    </span>
                    <button
                      type="button"
                      onClick={handleRemoveDiscount}
                      style={{
                        marginLeft: 'auto',
                        background: 'none',
                        border: 'none',
                        color: '#C62828',
                        cursor: 'pointer',
                        fontSize: '1.25rem',
                        padding: '0 0.25rem'
                      }}
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      type="text"
                      value={discountCode}
                      onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                      placeholder="Enter code"
                      style={{ flex: 1, textTransform: 'uppercase' }}
                    />
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleApplyDiscountCode}
                      disabled={!discountCode.trim() || discountValidating}
                    >
                      {discountValidating ? 'Checking...' : 'Apply'}
                    </button>
                  </div>
                )}
              </div>

              {/* Price Summary */}
              <div style={{
                marginTop: '1rem',
                padding: '1rem',
                background: '#FAF8F5',
                borderRadius: '8px',
                border: '1px solid #E8C9A5'
              }}>
                {selectedServices.map(service => (
                  <div key={service.id} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span>{service.name}</span>
                    <span>£{parseFloat(service.price).toFixed(2)}</span>
                  </div>
                ))}
                {selectedServices.length > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.5rem', borderTop: '1px solid #E8C9A5', marginBottom: discountApplied ? '0.5rem' : 0 }}>
                    <span style={{ fontWeight: 500 }}>Subtotal</span>
                    <span style={{ textDecoration: discountApplied ? 'line-through' : 'none', color: discountApplied ? '#999' : 'inherit', fontWeight: 500 }}>
                      £{totalPrice.toFixed(2)}
                    </span>
                  </div>
                )}
                {selectedServices.length === 1 && discountApplied && (
                  <div style={{ borderTop: '1px solid #E8C9A5', paddingTop: '0.5rem' }} />
                )}
                {discountApplied && (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#4A7C59', marginBottom: '0.5rem' }}>
                      <span>Discount ({discountApplied.code})</span>
                      <span>-£{discountApplied.discount_amount.toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, fontSize: '1.1rem', borderTop: '1px solid #E8C9A5', paddingTop: '0.5rem' }}>
                      <span>Total</span>
                      <span style={{ color: '#8B2635' }}>£{discountApplied.final_price.toFixed(2)}</span>
                    </div>
                  </>
                )}
              </div>

              {/* Saved Card Option */}
              {checkingCard && (
                <div style={{
                  marginTop: '1rem',
                  padding: '0.75rem',
                  background: '#FAF8F5',
                  borderRadius: '8px',
                  border: '1px solid #E8C9A5',
                  fontSize: '0.9rem',
                  color: '#6B6B6B',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}>
                  <CircularProgress size={18} sx={{ color: '#8B2635' }} />
                  Checking for saved card...
                </div>
              )}

              {savedCard && !checkingCard && (
                <div style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  background: '#F0F7F1',
                  borderRadius: '8px',
                  border: '1px solid #A8C9AD'
                }}>
                  <h4 style={{ margin: '0 0 0.75rem 0', color: '#4A7C59', fontSize: '1rem' }}>
                    💳 Saved Payment Method
                  </h4>
                  <p style={{ margin: '0 0 0.75rem 0', color: '#3D6B4A', fontSize: '0.9rem' }}>
                    We have your <strong>{savedCard.card_brand}</strong> card ending in <strong>{savedCard.card_last4}</strong> on file.
                  </p>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      padding: '0.5rem',
                      background: useSavedCard ? '#F0F7F1' : 'transparent',
                      borderRadius: '6px',
                      border: useSavedCard ? '2px solid #4A7C59' : '2px solid transparent'
                    }}>
                      <input
                        type="radio"
                        name="cardOption"
                        checked={useSavedCard}
                        onChange={() => setUseSavedCard(true)}
                        style={{ marginRight: '0.5rem' }}
                      />
                      <span style={{ color: '#4A7C59', fontWeight: useSavedCard ? '600' : '400' }}>
                        Use saved card ({savedCard.card_brand} •••• {savedCard.card_last4})
                      </span>
                    </label>

                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      padding: '0.5rem',
                      background: !useSavedCard ? '#F0F7F1' : 'transparent',
                      borderRadius: '6px',
                      border: !useSavedCard ? '2px solid #4A7C59' : '2px solid transparent'
                    }}>
                      <input
                        type="radio"
                        name="cardOption"
                        checked={!useSavedCard}
                        onChange={() => setUseSavedCard(false)}
                        style={{ marginRight: '0.5rem' }}
                      />
                      <span style={{ color: '#4A7C59', fontWeight: !useSavedCard ? '600' : '400' }}>
                        Use a different card
                      </span>
                    </label>
                  </div>
                </div>
              )}

              {/* Booking Summary */}
              <div style={{
                marginTop: '1.5rem',
                padding: '1rem',
                background: '#FAF8F5',
                borderRadius: '8px',
                border: '1px solid #E8E4E0'
              }}>
                <h4 style={{ margin: '0 0 0.5rem 0' }}>Booking Summary</h4>
                <p style={{ margin: '0.25rem 0' }}><strong>Services:</strong></p>
                {selectedServices.map(service => (
                  <p key={service.id} style={{ margin: '0.15rem 0 0.15rem 1rem', fontSize: '0.95rem' }}>
                    • {service.name} ({service.duration} min) - £{parseFloat(service.price).toFixed(2)}
                  </p>
                ))}
                <p style={{ margin: '0.5rem 0 0.25rem 0' }}><strong>Date:</strong> {selectedDate}</p>
                <p style={{ margin: '0.25rem 0' }}><strong>Time:</strong> {selectedSlot.start_time}</p>
                <p style={{ margin: '0.25rem 0', color: '#6B6B6B' }}><strong>Duration:</strong> {totalDuration} minutes</p>
                {discountApplied ? (
                  <div style={{ marginTop: '0.5rem' }}>
                    <p style={{ margin: '0.25rem 0', color: '#6B6B6B' }}>
                      <span style={{ textDecoration: 'line-through' }}>£{totalPrice.toFixed(2)}</span>
                      <span style={{ marginLeft: '0.5rem', color: '#4A7C59' }}>
                        -{discountApplied.discount_type === 'percentage' ? `${discountApplied.discount_value}%` : `£${discountApplied.discount_value}`}
                      </span>
                    </p>
                    <p style={{ margin: '0.25rem 0 0 0', fontSize: '1.25rem', color: '#4A7C59' }}>
                      <strong>Total: £{discountApplied.final_price.toFixed(2)}</strong>
                    </p>
                  </div>
                ) : (
                  <p style={{ margin: '0.5rem 0 0 0', fontSize: '1.25rem', color: '#8B2635' }}>
                    <strong>Total: £{totalPrice.toFixed(2)}</strong>
                  </p>
                )}
              </div>

              {/* No-show Policy Notice */}
              <div style={{
                marginTop: '1rem',
                padding: '0.75rem 1rem',
                background: '#FAF8F5',
                borderRadius: '8px',
                border: '1px solid #D4A574',
                fontSize: '0.9rem'
              }}>
                <strong>Note:</strong> {useSavedCard && savedCard
                  ? 'Your saved card will be used to secure this booking.'
                  : 'Card details are required to secure your booking.'}
                {' '}Your card will <strong>not</strong> be charged today. You pay at the salon after your appointment.
                <br />
                <span style={{ color: '#6B6B6B', fontSize: '0.85rem' }}>
                  If you need to cancel, please give at least 24 hours notice.
                </span>
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Processing...' : (useSavedCard && savedCard) ? 'Confirm Booking' : 'Continue to Card Details'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setStep(2)}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancelBooking}
                >
                  Cancel Booking
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

export default BookingPage;
